import type {
  RohsTestReportAuditStoredEntry,
  RohsTestReportAuditVerdict,
} from '@/shared/hooks/api/rohsComplianceFiles'

export function verdictBadgeVariant(
  verdict: RohsTestReportAuditVerdict
): 'success' | 'danger' | 'warning' | 'secondary' {
  if (verdict === 'PASS') {
    return 'success'
  }
  if (verdict === 'INCONCLUSIVE' || verdict === 'NO_TEST_REPORT') {
    return 'warning'
  }
  if (
    verdict === 'SUBJECT_MISMATCH' ||
    verdict === 'MISSING_SUBSTANCES' ||
    verdict === 'OVER_LIMIT' ||
    verdict === 'NOT_A_ROHS_TEST_REPORT'
  ) {
    return 'danger'
  }
  return 'secondary'
}

export function isRohsTestReportAuditPass(
  verdict: RohsTestReportAuditVerdict
): boolean {
  return verdict === 'PASS'
}

export function auditResultCardClassName(
  verdict: RohsTestReportAuditVerdict | null,
  options: { running?: boolean; empty?: boolean }
): string {
  if (options.running) {
    return 'border-border bg-muted/40'
  }
  if (options.empty || verdict == null) {
    return 'border-dashed border-border bg-card'
  }
  const badgeVariant = verdictBadgeVariant(verdict)
  if (badgeVariant === 'success') {
    return 'border-emerald-200 bg-emerald-50/70 dark:border-emerald-800/80 dark:bg-emerald-950/35'
  }
  if (badgeVariant === 'warning') {
    return 'border-amber-200 bg-amber-50/70 dark:border-amber-800/80 dark:bg-amber-950/35'
  }
  if (badgeVariant === 'danger') {
    return 'border-destructive/35 bg-destructive/5'
  }
  return 'border-border bg-card'
}

export function auditResultMediaClassName(
  verdict: RohsTestReportAuditVerdict | null,
  options: { running?: boolean; empty?: boolean }
): string {
  if (options.running) {
    return 'bg-muted text-muted-foreground'
  }
  if (options.empty || verdict == null) {
    return 'bg-muted text-muted-foreground'
  }
  const badgeVariant = verdictBadgeVariant(verdict)
  if (badgeVariant === 'success') {
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200'
  }
  if (badgeVariant === 'warning') {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200'
  }
  if (badgeVariant === 'danger') {
    return 'bg-destructive/10 text-destructive'
  }
  return 'bg-muted text-foreground'
}

export function formatAuditTimestamp(timestamp: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(timestamp))
}

export function auditReasoning(
  entry: RohsTestReportAuditStoredEntry,
  locale: string
): string {
  if (locale.startsWith('zh')) {
    return entry.reasoningZh || entry.reasoningEn
  }
  return entry.reasoningEn
}
